import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import { CircularProgress, Grid, TextField, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { injectIntl } from 'react-intl';
import ReactQuill from 'react-quill';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import SimpleAutoComplete from '../../../../../components/auto-complete/autocomplete';

// Modal de criação de chamado a partir de uma conversa ATIVA (omnichannel /2talk-chat).
//
// PERFORMANCE: o estado dos campos (modo/título/descrição/switch) vive AQUI (local),
// não no index.js. Isso é proposital — se subíssemos cada tecla pro componente de topo,
// toda digitação re-renderizaria a tela inteira do omnichannel (header + lista completa
// de mensagens + sidebars), causando travamento. Aqui, digitar só re-renderiza o modal.
// O valor coletado sobe UMA única vez, no confirmar, via props.onConfirm(payload).
function DialogCreateCalled(props) {
  const intl = props.intl;

  const [mode, setMode] = useState('standard');               // 'standard' | 'custom'
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // Histórico da conversa: 4 modos (both | description | accompaniments | none). Default 'both'.
  const [historyMode, setHistoryMode] = useState('both');
  // Responsável do chamado: 'me' = você (logado; só se role.id===5) | 'other' = agente do canal | 'none'.
  const [assignedMode, setAssignedMode] = useState('me');
  const [assignedAgent, setAssignedAgent] = useState(null);

  // Reinicializa os campos sempre que o modal abre.
  useEffect(() => {
    if (props.open) {
      setMode('standard');
      setTitle('');
      setDescription('');
      setHistoryMode('both');
      // Default 'Você' (quando o logado é técnico, role.id===5); senão cai pra 'Nenhum'.
      setAssignedMode(props.canAssignSelf ? 'me' : 'none');
      setAssignedAgent(null);
    }
  }, [props.open, props.canAssignSelf]);

  const reactQuillModules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'image'],
      ['clean'],
    ],
  };

  const reactQuillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image', 'color', 'font'
  ];

  const confirm = () => {
    // attachMessagesAs carrega os 4 modos de histórico (both|description|accompaniments|none).
    // assignedMode controla o responsável; assignedUserId só importa quando 'other'.
    props.onConfirm({
      mode, title, description, attachMessagesAs: historyMode,
      assignedMode,
      assignedUserId: assignedMode === 'other' && assignedAgent ? assignedAgent.id : null,
    });
  };

  const formatCard = (value, Icon, titleId, descriptionId) => {
    const selected = mode === value;
    return (
      <Grid item xs={12} sm={6}>
        <div
          onClick={() => setMode(value)}
          style={{
            cursor: 'pointer',
            border: '1px solid #e0e0e0',
            borderLeft: selected ? '4px solid #8BC34A' : '4px solid #e0e0e0',
            borderRadius: '12px',
            padding: '14px 16px',
            height: '100%',
            backgroundColor: selected ? '#f6fbef' : '#fff',
            transition: 'all .15s ease',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Icon style={{ color: selected ? '#689F38' : '#9e9e9e', fontSize: 20 }} />
            <Typography style={{ fontSize: 15, fontWeight: 600, color: '#222' }}>
              {intl.formatMessage({ id: titleId })}
            </Typography>
          </div>
          <Typography style={{ fontSize: 13, color: '#888' }}>
            {intl.formatMessage({ id: descriptionId })}
          </Typography>
        </div>
      </Grid>
    );
  };

  return (
    <Dialog
      open={props.open}
      aria-labelledby="create-called-dialog-title"
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh', borderRadius: '12px' } }}
    >
      <DialogTitle id="create-called-dialog-title">
        {intl.formatMessage({ id: "create.called.modal.title" })}
      </DialogTitle>
      {/* dividers torna o DialogContent a ÚNICA região rolável (flex + overflow gerenciados pelo MUI),
          cobrindo TODO o conteúdo até o último bloco (responsável). Sem o cap de 70vh no div interno,
          que antes cortava o final sem barra de rolagem confiável. */}
      <DialogContent dividers sx={{ overflowY: 'auto' }}>
        <div style={{ paddingRight: '8px' }}>
          <DialogContentText sx={{ mb: 2 }}>
            {intl.formatMessage({ id: "create.called.modal.subtitle" })}
          </DialogContentText>

          <Typography style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 8 }}>
            {intl.formatMessage({ id: "create.called.format" })}
          </Typography>
          <Grid container spacing={2}>
            {formatCard('standard', DescriptionOutlinedIcon, 'create.called.format.standard', 'create.called.format.standard.info')}
            {formatCard('custom', TuneOutlinedIcon, 'create.called.format.custom', 'create.called.format.custom.info')}
          </Grid>

          {mode === 'custom' && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label={intl.formatMessage({ id: "create.called.title.label" })}
                  InputLabelProps={{ shrink: true }}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography style={{ fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 6 }}>
                  {intl.formatMessage({ id: "create.called.description.label" })}
                </Typography>
                {/* Wrapper com altura fixa reserva editor (180) + toolbar (~50). Sem isso, o
                    .ql-container do ReactQuill transborda e fica POR CIMA do select de histórico abaixo. */}
                <div style={{ height: 230 }}>
                  <ReactQuill
                    value={description}
                    style={{ height: 180 }}
                    modules={reactQuillModules}
                    formats={reactQuillFormats}
                    onChange={(content) => setDescription(content)}
                  />
                </div>
              </Grid>
            </Grid>
          )}

          {/* Histórico da conversa — vale pros DOIS modos (padrão e personalizado). Default 'Ambos'. */}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <div style={{ border: '1px solid #e0e0e0', borderRadius: '12px', padding: '12px 16px' }}>
                <Typography style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 8 }}>
                  {intl.formatMessage({ id: "create.called.history.label" })}
                </Typography>
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  size="small"
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                  value={historyMode}
                  onChange={(e) => setHistoryMode(e.target.value)}
                >
                  <option value="both">{intl.formatMessage({ id: "create.called.history.both" })}</option>
                  <option value="description">{intl.formatMessage({ id: "create.called.history.description" })}</option>
                  <option value="accompaniments">{intl.formatMessage({ id: "create.called.history.accompaniments" })}</option>
                  <option value="none">{intl.formatMessage({ id: "create.called.history.none" })}</option>
                </TextField>
                <Typography style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                  {intl.formatMessage({ id: "create.called.history.info" })}
                </Typography>
              </div>
            </Grid>
          </Grid>

          {/* Responsável do chamado — o agente do chat (tempo real) nem sempre é quem vai tratar o
              chamado (assíncrono). 'Você' só aparece se o logado for técnico (role.id===5). */}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <div style={{ border: '1px solid #e0e0e0', borderRadius: '12px', padding: '12px 16px' }}>
                <Typography style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 8 }}>
                  {intl.formatMessage({ id: "create.called.responsible.label" })}
                </Typography>
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  size="small"
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                  value={assignedMode}
                  onChange={(e) => setAssignedMode(e.target.value)}
                >
                  {props.canAssignSelf && <option value="me">{intl.formatMessage({ id: "create.called.responsible.me" })}</option>}
                  <option value="other">{intl.formatMessage({ id: "create.called.responsible.other" })}</option>
                  <option value="none">{intl.formatMessage({ id: "create.called.responsible.none" })}</option>
                </TextField>

                {assignedMode === 'other' && (
                  <div style={{ marginTop: 10 }}>
                    <SimpleAutoComplete
                      label={intl.formatMessage({ id: "create.called.responsible.other.placeholder" })}
                      options={props.channelAgents || []}
                      stateName="assignedAgent"
                      changeSelect={(name, value) => setAssignedAgent(value && value.id > 0 ? value : null)}
                      selected={assignedAgent || { id: 0, name: '' }}
                      size="small"
                    />
                  </div>
                )}

                {assignedMode === 'none' && (
                  <Typography style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                    {intl.formatMessage({ id: "create.called.responsible.none.info" })}
                  </Typography>
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => props.onClose()} color="primary" disabled={props.loading}>
          {intl.formatMessage({ id: "cancel" })}
        </Button>
        <Button
          onClick={confirm}
          variant="contained"
          color="primary"
          disabled={props.loading}
          startIcon={props.loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {intl.formatMessage({ id: "confirm" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default injectIntl(DialogCreateCalled);
